/**
 * Site-wide banner that renders when super-admin has paused orders.
 * Polls `/api/system/pause-status` (no auth needed) every 30s so it
 * appears on marketing pages + patient dashboard automatically, and
 * clears on the same cadence as the mobile banner (web-mobile parity).
 *
 * When paused it ALSO exposes an inline "notify me when we're back"
 * email capture that POSTs to /api/system/pause-notify. Admin views
 * the collected list from System Ops.
 */
import { useEffect, useState } from "react";
import API from "@/lib/api";
import { AlertTriangle, X, Check } from "lucide-react";

export default function OrdersPausedBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [hiddenByModal, setHiddenByModal] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signedUp, setSignedUp] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        // Use the shared @/lib/api instance so this works on the custom
        // production domain (glpflo.org). A raw axios call against
        // process.env.REACT_APP_BACKEND_URL was cross-origin from
        // glpflo.org and got CORS-blocked, so the banner never rendered.
        //
        // Cache-bust each GET with `?t=${Date.now()}` and send
        // `Cache-Control: no-store` so Cloudflare / service-worker /
        // browser disk cache never serves a stale paused-state. Matches
        // the mobile banner (which hit a hard cache bug on TestFlight).
        const { data } = await API.get(`/system/pause-status?t=${Date.now()}`, {
          headers: { "Cache-Control": "no-store", Pragma: "no-cache" },
        });
        if (!cancelled) setStatus(data);
      } catch { /* silent */ }
    };
    poll();
    // 30s cadence — paused/resumed events surface within half a minute
    // of the admin flipping the toggle. Mirrors mobile parity.
    const id = setInterval(poll, 30_000);
    // Also refetch when the tab becomes visible again (patient left the
    // tab open for a while, came back, we don't want stale state).
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    return () => {
      cancelled = true;
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  // Auto-hide while a full-screen modal (e.g. Fit Quiz) is open so the
  // top-of-page stack doesn't crowd the modal chrome. The modal
  // dispatches window events on open/close.
  useEffect(() => {
    const onOpen = () => setHiddenByModal(true);
    const onClose = () => setHiddenByModal(false);
    window.addEventListener("glpflo:modal-open", onOpen);
    window.addEventListener("glpflo:modal-close", onClose);
    return () => {
      window.removeEventListener("glpflo:modal-open", onOpen);
      window.removeEventListener("glpflo:modal-close", onClose);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim() || submitting) return;
    setSubmitting(true);
    try {
      await API.post("/system/pause-notify", { email: email.trim() });
      setSignedUp(true);
      setEmail("");
    } catch {
      // Non-blocking. User can retry from the same field.
    } finally {
      setSubmitting(false);
    }
  };

  if (!status?.paused || dismissed || hiddenByModal) return null;

  return (
    <div className="sticky top-0 z-[90] w-full bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 text-amber-950 shadow-md" data-testid="orders-paused-banner">
      <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-3 px-4 py-2.5">
        <AlertTriangle className="w-5 h-5 text-amber-900 shrink-0" />
        <p className="text-sm font-medium flex-1 min-w-[240px]">
          <span className="font-bold mr-2">System notice:</span>
          {status.message}
        </p>

        {signedUp ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-800 bg-emerald-100/80 rounded-full px-2.5 py-1" data-testid="pause-notify-thanks">
            <Check className="w-3.5 h-3.5" /> You&rsquo;re on the list
          </span>
        ) : (
          <form onSubmit={submit} className="flex items-center gap-1.5" data-testid="pause-notify-form">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email me when back"
              className="h-8 rounded-md border border-amber-700/40 bg-white/90 px-2.5 text-xs w-52 text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-900/40"
              data-testid="pause-notify-input"
            />
            <button
              type="submit"
              disabled={submitting}
              className="h-8 rounded-md bg-amber-900 hover:bg-amber-950 text-white text-xs font-semibold px-3 disabled:opacity-60"
              data-testid="pause-notify-submit"
            >
              {submitting ? "…" : "Notify me"}
            </button>
          </form>
        )}

        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="w-6 h-6 rounded-full bg-amber-100/70 hover:bg-white flex items-center justify-center shrink-0"
          data-testid="orders-paused-banner-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
